import React, { useMemo } from "react";
import { Card, CardContent, CardHeader } from "@/components/Card/Card";
import { PercentageOfNewCustomersChangeByZipCodeDataItem } from "@/modules/stochastic/features/SalesAndCustomerDashboard/api/fetchStochasticDashboardData/types";

const extractYearsFromData = (
  data: PercentageOfNewCustomersChangeByZipCodeDataItem[],
): string[] => {
  const years = new Set<string>();
  data.forEach((item) => {
    Object.entries(item).forEach(([key, value]) => {
      if (key !== "postalCode" && typeof value === "object" && value !== null) {
        years.add(key);
      }
    });
  });
  return Array.from(years).sort();
};

interface PercentageOfNewCustomersByZipCodeTableProps {
  initialData: PercentageOfNewCustomersChangeByZipCodeDataItem[];
}

export const PercentageOfNewCustomersChangeByZipCodeTable: React.FC<
  PercentageOfNewCustomersByZipCodeTableProps
> = ({ initialData }) => {
  const allYears = useMemo(
    () => extractYearsFromData(initialData),
    [initialData],
  );

  return (
    <Card className="w-full">
      <CardHeader></CardHeader>
      <CardContent className="pb-10">
        <div className="w-full overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm h-[450px] dark:[&::-webkit-scrollbar]:w-2 dark:[&::-webkit-scrollbar]:h-2 dark:[&::-webkit-scrollbar-track]:bg-gray-800 dark:[&::-webkit-scrollbar-thumb]:bg-primary dark:[&::-webkit-scrollbar-thumb]:rounded-full dark:[&::-webkit-scrollbar-corner]:bg-gray-800">
          <table className="w-full caption-bottom text-sm min-w-max">
            <thead className="[&_tr]:border-b">
              <tr className="bg-gray-50/75 dark:bg-gray-800/50">
                <th className="h-11 px-4 align-middle font-semibold text-muted-foreground text-center border-r border-gray-200 dark:border-white/20 bg-gray-50/75 dark:bg-gray-800/50 uppercase text-xs tracking-wider" />
                {allYears.map((year) => (
                  <th
                    className="h-11 px-4 align-middle font-semibold text-muted-foreground text-center border-r border-gray-200 dark:border-white/20 bg-gray-50/75 dark:bg-gray-800/50 uppercase text-xs tracking-wider"
                    colSpan={2}
                    key={year}
                  >
                    {year}
                  </th>
                ))}
              </tr>
              <tr className="bg-white dark:bg-gray-800/30">
                <th className="h-11 px-4 align-middle font-semibold text-muted-foreground text-center border-r border-gray-200 dark:border-white/20 uppercase text-xs tracking-wider">
                  ZIP Code
                </th>
                {allYears.map((year, index) => (
                  <React.Fragment key={year}>
                    <th className="h-11 pl-4 pr-6 align-middle font-semibold text-muted-foreground text-center border-gray-200 dark:border-gray-700 whitespace-nowrap uppercase text-xs tracking-wider">
                      % Change
                    </th>
                    <th
                      className={`h-11 pl-6 pr-4 align-middle font-semibold text-muted-foreground text-center border-gray-200 dark:border-white/20 whitespace-nowrap uppercase text-xs tracking-wider ${
                        index === allYears.length - 1 ? "" : "border-r"
                      }`}
                    >
                      # New Customer
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {initialData.map((row, rowIndex) => (
                <tr
                  className="border-b border-gray-200 dark:border-gray-700 transition-colors hover:bg-muted/50"
                  key={rowIndex}
                >
                  <td className="px-4 py-3 align-middle text-left text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-white/20 whitespace-nowrap font-semibold">
                    {row.postalCode}
                  </td>
                  {allYears.map((year, index) => {
                    const entry = row[year] as
                      | { ncCount: number; percentChange: number | null }
                      | undefined;

                    const change =
                      entry?.percentChange != null
                        ? `${entry.percentChange.toFixed(2)}%`
                        : "-";
                    const count = entry?.ncCount ?? "-";

                    return (
                      <React.Fragment key={year}>
                        <td className="px-4 py-3 text-right text-gray-900 dark:text-white whitespace-nowrap">
                          {change}
                        </td>
                        <td
                          className={`px-4 py-3 text-right text-gray-900 dark:text-white whitespace-nowrap ${
                            index === allYears.length - 1
                              ? ""
                              : "border-r border-gray-200 dark:border-white/20"
                          }`}
                        >
                          {count}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
